import { AbstractControl, ValidatorFn } from '@angular/forms';
import { EnvironmentModel } from '@fh-client-interaction-frontends/fh-app-config';
import { PARTNERSHIP } from '@fh-client-interaction-frontends/fh-providers';

export class FhFormValidators {

  static required(field: string): ValidatorFn {
    const required: ValidatorFn = (control: AbstractControl): { [key: string]: any } | null => {
      if (control.value === undefined || control.value === null || control.value === '') {
        return { error: 'fields.' + field + '.validations.required' };
      }
      return null;
    };
    (required as any).validatorName = 'required';
    return required;
  }

  static isNumber(field: string, range?: { min?: number; max?: number }, integerOnlyPercentile?: boolean): ValidatorFn {
    const isNumber: ValidatorFn = (control: AbstractControl): { [key: string]: any } | null => {
      if (!control.value) {
        return null;
      }
      if (integerOnlyPercentile) {
        // this regex accepts only integer numbers, only from 0 to 100 used for percentile from 0 to 100
        if (new RegExp('^(100|0*([0-9]|[1-9][0-9])?)$').test(control.value)) {
          return null;
        } else {
          return { error: 'fields.' + field + '.validations.invalid' };
        }
      }
      const numberRangeRegex = /^\d+(?:[.,]\d+)?$/;
      if (new RegExp(numberRangeRegex).test(control.value)) {
        if (range?.min && +control.value < range?.min) {
          return { error: 'fields.' + field + '.validations.invalid' };
        }
        if (range?.max && +control.value > range?.max) {
          return { error: 'fields.' + field + '.validations.invalid' };
        }
        return null;
      }
      return { error: 'fields.' + field + '.validations.invalid' };
    };

    (isNumber as any).validatorName = 'isNumber';
    return isNumber;
  }

  static isLocation(field: string): ValidatorFn {
    const isLocation: ValidatorFn = (control: AbstractControl): { [key: string]: any } | null => {
      if (!control.value) {
        return null;
      }

      // Regex to allow only letters (including accented ones) and spaces
      const locationRegex = /^[A-Za-zÀ-ÿ\s]+$/;

      if (locationRegex.test(control.value)) {
        return null;
      }

      return { error: 'fields.' + field + '.validations.invalid' };
    };

    (isLocation as any).validatorName = 'isLocation';
    return isLocation;
  }

  static isPostalCode(field: string): ValidatorFn {
    const isPostalCode: ValidatorFn = (control: AbstractControl): { [key: string]: any } | null => {
      if (!control.value) {
        return null;
      }

      // Regex for postal code format: 4 digits (1-9 followed by any digit) + dash + 3 digits
      const postalCodeRegex = /^[1-9]\d{3}-\d{3}$/;

      if (postalCodeRegex.test(control.value)) {
        return null;
      }

      return { error: 'fields.' + field + '.validations.invalid' };
    };

    (isPostalCode as any).validatorName = 'isPostalCode';
    return isPostalCode;
  }

  static personalIdentificationNumber(field: string): ValidatorFn {
    const personalIdentificationNumber: ValidatorFn = (control: AbstractControl): { [key: string]: any } | null => {
      if (!control.value) {
        return null;
      }

      // Regex for personal identification number
      const pinRegex = /^\d{8}$/;

      if (pinRegex.test(control.value)) {
        return null;
      }

      return { error: 'fields.' + field + '.validations.invalid' };
    };

    (personalIdentificationNumber as any).validatorName = 'personalIdentificationNumber';
    return personalIdentificationNumber;
  }

  static isCurrency(field: string): ValidatorFn {
    const isCurrency: ValidatorFn = (control: AbstractControl): { [key: string]: any } | null => {
      if (!control.value) {
        return null;
      }

      const currencyRegex = /^\d{1,3}(\.\d{3})*(,\d{2})?$/;

      if (currencyRegex.test(control.value)) {
        return null;
      }

      return { error: 'fields.' + field + '.validations.invalid' };
    };

    (isCurrency as any).validatorName = 'isCurrency';
    return isCurrency;
  }

  static consentRequired(field: string): ValidatorFn {
    const consentRequired: ValidatorFn = (control: AbstractControl): { [key: string]: any } | null => {
      return control.value === true ? null : { error: 'fields.' + field + '.validations.required' };
    };

    (consentRequired as any).validatorName = 'consentRequired';
    return consentRequired;
  }

  static password(field: string): any {
    const password: ValidatorFn = (control: AbstractControl): { [key: string]: any } | null => {
      // const pattern = new RegExp('^(?=^.{8,}$)(?!.* )([0-9a-zA-Z])');
      const pattern = new RegExp('^(?=.*?[A-Z])(?=.*?[a-z])(?=.*?[0-9])(?=^\\S*$)(?=.*?[#?!@$%^&*-]).{8,}$');
      if (control.value.length < 8) {
        return { error: 'fields.' + field + '.validations.length' };
      }
      if (!pattern.test(control.value)) {
        return { error: 'fields.' + field + '.validations.pattern_mismatch' };
      }
      return null;
    };

    (password as any).validatorName = 'password';
    return password;
  }

  // Checks if the field passes on a given RegExp
  static pattern(field: string, pattern: RegExp): ValidatorFn {
    const patternValidator: ValidatorFn = (control: AbstractControl): { [key: string]: any } | null => {
      if (!control.value) {
        return null;
      }
      return !pattern.test(control.value) ? { error: 'fields.' + field + '.validations.pattern_mismatch' } : null;
    };

    (patternValidator as any).validatorName = 'pattern';
    return patternValidator;
  }

  static mustMatch(field: string, compareToFieldName: string): ValidatorFn {
    const mustMatch: ValidatorFn = (control: AbstractControl): { [key: string]: any } | null => {
      const targetFieldValue = control.parent?.get(compareToFieldName)?.value;
      return control.value === targetFieldValue ? null : { error: 'fields.' + field + '.validations.mustMatch' };
    };

    (mustMatch as any).validatorName = 'mustMatch';
    return mustMatch;
  }

  // Checks if a date is valid
  static dateValidator(field: string): ValidatorFn {
    const dateValidator: ValidatorFn = (dateControl: AbstractControl): { [key: string]: any } | null => {
      if (!dateControl.value || !isNaN(new Date(dateControl.value).getTime())) {
        return null;
      } else {
        return { error: 'fields.' + field + '.validations.invalid' };
      }
    };

    (dateValidator as any).validatorName = 'dateValidator';
    return dateValidator;
  }

  // Checks if the date is in between a certain range of ages
  static dateAgeRangeValidator(field: string, ages: { min?: number; max?: number }): ValidatorFn {
    const dateAgeRangeValidator: ValidatorFn = (dateControl: AbstractControl): { [key: string]: any } | null => {
      const birthDate = new Date(dateControl.value);
      const today = new Date();
      const age = today.getFullYear() - birthDate.getFullYear();

      // If years difference is over 18 OR it is equal to 18 and his birthdate has been celebrated this year
      if (
        (ages.min && age < ages.min) ||
        (age === ages.min && (birthDate.getMonth() > today.getMonth() || (birthDate.getMonth() === today.getMonth() && birthDate.getDate() > today.getDate())))
      ) {
        return { error: 'fields.' + field + '.validations.out_of_range_min' };
      }

      if (
        (ages.max && age > ages.max) ||
        (age === ages.max && (birthDate.getMonth() < today.getMonth() || (birthDate.getMonth() === today.getMonth() && birthDate.getDate() < today.getDate())))
      ) {
        return { error: 'fields.' + field + '.validations.out_of_range_max' };
      }

      return null;
    };

    (dateAgeRangeValidator as any).validatorName = 'dateAgeRangeValidator';
    return dateAgeRangeValidator;
  }

  static dateRangeValidator(field: string, range: { min?: Date; max?: Date }): ValidatorFn {
    const dateRangeValidator: ValidatorFn = (dateControl: AbstractControl): { [key: string]: any } | null => {
      const date = new Date(dateControl.value);
      // Set hours to min and max on respective properties so hours do not mess up our ifs
      range.min?.setHours(0, 0, 0, 0);
      range.max?.setHours(23, 59, 59, 59);

      if (range.min && date.getTime() < range.min.getTime()) {
        return { error: 'fields.' + field + '.validations.out_of_range_min' };
      }

      if (range.max && date.getTime() > range.max.getTime()) {
        return { error: 'fields.' + field + '.validations.out_of_range_max' };
      }

      return null;
    };

    (dateRangeValidator as any).validatorName = 'dateRangeValidator';
    return dateRangeValidator;
  }

  static dateRangeValidatorAuthorizations(field: string, range: { min?: Date; max?: any }): ValidatorFn {
    const dateRangeValidatorAuthorizations: ValidatorFn = (
      dateValidator: AbstractControl
    ): {
      [key: string]: any;
    } | null => {
      const date = new Date(dateValidator.value);
      if(range.max){
      range.max = new Date(new Date().setDate(new Date().getDate() + range.max));
      }

      range.min?.setHours(0, 0, 0, 0);
      range.max?.setHours(23, 59, 59, 59);

      if (range.min && date.getTime() < range.min.getTime()) {
        return { error: 'fields.' + field + '.validations.out_of_range_min' };
      }

      if (range.max && date.getTime() > range.max.getTime()) {
        return { error: 'fields.' + field + '.validations.invalid' };
      }

      return null;
    };

    (dateRangeValidatorAuthorizations as any).validatorName = 'dateRangeValidatorAuthorizations';
    return dateRangeValidatorAuthorizations;
  }

  static contractStartDateValidator(field: string, environment: EnvironmentModel): ValidatorFn {
    const contractStartDateValidator: ValidatorFn = (dateValidator: AbstractControl): { [key: string]: any } | null => {
      const date = new Date(dateValidator.value);
      let range = {
        min: new Date(),
        max: new Date(new Date().setDate(new Date().getDate() + environment.maxContractStartDaysRange))
      };

      range.min?.setHours(0, 0, 0, 0);
      range.max?.setHours(23, 59, 59, 59);

      if (range.min && date.getTime() < range.min.getTime()) {
        return { error: 'fields.' + field + '.validations.out_of_range_min' };
      }

      if (range.max && date.getTime() > range.max.getTime()) {
        return { error: 'fields.' + field + '.validations.invalid' };
      }

      return null;
    };

    (contractStartDateValidator as any).validatorName = 'contractStartDateValidator';
    return contractStartDateValidator;
  }

  // Check if date is not greater than today
  static dateAfterToday(field: string): ValidatorFn {
    const dateAfterToday: ValidatorFn = (dateControl: AbstractControl): { [key: string]: any } | null => {
      return new Date(dateControl.value) > new Date() ? { error: 'fields.' + field + '.validations.greater_than_today' } : null;
    };

    (dateAfterToday as any).validatorName = 'dateAfterToday';
    return dateAfterToday;
  }

  static emailValidator(field: string): ValidatorFn {
    const emailValidator: ValidatorFn = (emailControl: AbstractControl): { [key: string]: any } | null => {
      if (!emailControl.value) {
        return null;
      }
      return new RegExp('^[a-zA-Z0-9\\.\\_\\-\\+]+@[a-zA-Z0-9\\-]+(\\.[a-zA-Z\\-]+)*\\.[a-zA-Z\\.]{2,}$').test(emailControl.value)
        ? null
        : { error: 'fields.' + field + '.validations.pattern_mismatch' };
    };

    (emailValidator as any).validatorName = 'emailValidator';
    return emailValidator;
  }

  // Check if phone number starts with the allowed combination and has a length of 9 numbers
  static phoneValidator(field: string, length: any = 9, prefixRestrictions: Array<string> = []): ValidatorFn {
    const partnership = PARTNERSHIP;

    if(length == 9) {
      length = partnership.defaults.phoneLength;
    }

    if(prefixRestrictions.length == 0) {
      prefixRestrictions = partnership.defaults.prefixRestrictions
    }

    const phoneValidator: ValidatorFn = (phoneControl: AbstractControl): { [key: string]: any } | null => {
      if (!phoneControl.value) {
        return null;
      }

      const numericRegex = /^\d+$/;
      if (!numericRegex.test(phoneControl.value)) {
        return { error: 'fields.' + field + '.validations.invalid' };
      }

      const validLengths = Array.isArray(length) ? length : [length];
      if (!validLengths.includes(phoneControl.value.length)) {
        return { error: 'fields.' + field + '.validations.invalid' }; // Check for valid length
      }

      if (prefixRestrictions.length > 0) {
        const valid = prefixRestrictions.find((restriction: string) => restriction === phoneControl.value.slice(0, restriction.length));
        return valid ? null : { error: 'fields.' + field + '.validations.invalid' };
      } else {
        return null;
      }
    };

    (phoneValidator as any).validatorName = 'phoneValidator';
    return phoneValidator;
  }

  // Country-aware phone validator: uses partnership defaults for default country, allows up to 15 digits for others
  static phoneValidatorByCountry(
    phoneCodeField: string,
    field: string,
    defaultCountryCode: string,
    phoneLength: number[] = [],
    prefixRestrictions: string[] = []
  ): ValidatorFn {
    const phoneValidatorByCountry: ValidatorFn = (control: AbstractControl): { [key: string]: any } | null => {
      const phone = control.value;
      const country = control.parent?.get(phoneCodeField)?.value;

      if (!phone) return null;

      if (!/^\d+$/.test(phone)) {
        return { error: 'fields.' + field + '.validations.invalid' };
      }

      if (country === defaultCountryCode && phoneLength.length > 0) {
        if (!phoneLength.includes(phone.length)) {
          return { error: 'fields.' + field + '.validations.invalid' };
        }
        if (prefixRestrictions.length > 0) {
          const valid = prefixRestrictions.find((r: string) => phone.startsWith(r));
          return valid ? null : { error: 'fields.' + field + '.validations.invalid' };
        }
        return null;
      }

      return phone.length > 15
        ? { error: 'fields.' + field + '.validations.invalid' }
        : null;
    };

    (phoneValidatorByCountry as any).validatorName = 'phoneValidatorByCountry';
    return phoneValidatorByCountry;
  }

  static faxValidator(field: string, length: any = 9): ValidatorFn {
    const faxValidator: ValidatorFn = (phoneControl: AbstractControl): { [key: string]: any } | null => {
      if (!phoneControl.value) {
        return null;
      }
      if (!length.includes(phoneControl.value.length)) {
        return { error: 'fields.' + field + '.validations.invalid' };
      } else {
        return null;
      }
    };

    (faxValidator as any).validatorName = 'faxValidator';
    return faxValidator;
  }

  // Check if phone prefix is one of the allowed ones
  static phonePrefixValidator(field: string): ValidatorFn {
    const allowed = ['+351'];

    const phonePrefixValidator: ValidatorFn = (phonePrefixControl: AbstractControl): { [key: string]: any } | null => {
      return allowed.includes(phonePrefixControl.value) ? null : { error: 'validations.' + field + '.invalid' };
    };

    (phonePrefixValidator as any).validatorName = 'phonePrefixValidator';
    return phonePrefixValidator;
  }

  // validates NISS
  static nissValidator(field: string): ValidatorFn {
    const nissValidator: ValidatorFn = (NissControl: AbstractControl): { [key: string]: any } | null => {
      const niss = NissControl.value;

      if (!niss) {
        return null;
      }

      if (niss.length !== 11) {
        return { error: 'fields.' + field + '.validations.invalid' };
      } else {
        const factors = [29, 23, 19, 17, 13, 11, 7, 5, 3, 2];
        const nissArray = [];
        for (let i = 0; i < niss.length; i++) {
          nissArray[i] = niss.charAt(i);
        }
        let sum = 0;
        // faz a soma do digito [j] x o dígito [j] do array dos fatores
        for (let j = 0; j < factors.length; j++) {
          sum += nissArray[j] * factors[j];
        }

        // verifica se dá resto 0
        if (Number(nissArray[nissArray.length - 1]) === 9 - (sum % 10)) {
          return null;
        } else {
          return { error: 'fields.' + field + '.validations.invalid' };
        }
      }
    };

    (nissValidator as any).validatorName = 'nissValidator';
    return nissValidator;
  }

  // validates Tax Number
  static taxNumberValidator(countryCode: any, field: string, filter?: 'company' | 'personal'): ValidatorFn {
    const taxNumberValidator: ValidatorFn = (control: AbstractControl): { [key: string]: any } | null => {
      const tax = control.value;
      let country = control.parent?.get(countryCode)?.value;

      if (!country) {
        country = countryCode;
      }

      if (!tax) {
        return null;
      }

      const cleanTax = tax.replace(/[\s\-\.]/g, '');

      switch (country) {
        case 'PT':
          return validatePT(cleanTax, field, filter);

        case 'RO':
          return validateRO(cleanTax, field);

        case 'ES':
          return validateES(cleanTax, field, filter);

        case 'EC':
          return validateEC(cleanTax, field);

        case 'UK':
        case 'GB':
          return validateUK(cleanTax.toUpperCase(), field, filter);

        case 'DE':
          return validateDE(cleanTax, field);

        case 'BR':
          return validateBR(cleanTax, field);

        case 'FR':
          return validateFR(cleanTax, field, filter);

        default:
          return null;
      }
    };

    // Função auxiliar para validar Portugal
    function validatePT(tax: string, field: string, filter?: 'company' | 'personal'): { [key: string]: any } | null {
      const allowed: Array<string> = [];
      if (filter === 'company') {
        allowed.push('5', '6', '8');
      } else if (filter === 'personal') {
        allowed.push('1', '2', '3', '45', '8', '98');
      } else {
        allowed.push('1', '2', '3', '45', '5', '6', '7', '8', '90', '91', '98', '99');
      }

      if (tax.length !== 9) {
        return { error: 'fields.' + field + '.validations.invalid' };
      }

      if (!allowed.some((item: any) => tax.startsWith(item))) {
        return { error: 'fields.' + field + '.validations.invalid' };
      }

      const total = Number(tax[0]) * 9 + Number(tax[1]) * 8 + Number(tax[2]) * 7 +
        Number(tax[3]) * 6 + Number(tax[4]) * 5 + Number(tax[5]) * 4 +
        Number(tax[6]) * 3 + Number(tax[7]) * 2;
      const modulo11 = total % 11;
      const checkDigit = modulo11 < 2 ? 0 : 11 - modulo11;

      return checkDigit === Number(tax[8]) ? null : { error: 'fields.' + field + '.validations.invalid' };
    }

    // Função auxiliar para validar Romênia
    function validateRO(tax: string, field: string): { [key: string]: any } | null {
      if (tax.length > 13 || tax.length < 7) {
        return { error: 'fields.' + field + '.validations.invalid' };
      }
      return null;
    }

    // Função auxiliar para validar Espanha
    function validateES(tax: string, field: string, filter?: 'company' | 'personal'): { [key: string]: any } | null {
      const cleanedTax = tax.replace(/^ES/i, '');

      if (cleanedTax.length !== 9) {
        return { error: 'fields.' + field + '.validations.invalid' };
      }

      // Define os regex patterns segundo o formato ANN NNN NNA
      const generalFormat = /^[A-Z0-9][0-9]{7}[A-Z0-9]$/;

      // Formatos específicos mais comuns
      const nifRegex = /^[0-9]{8}[A-Z]$/;  // Pessoas físicas (NIF)
      const cifRegex = /^[A-HJ-NP-SUVW][0-9]{7}[0-9A-J]$/;  // Empresas (CIF)
      const nieRegex = /^[XYZ][0-9]{7}[A-Z]$/;  // Estrangeiros (NIE)

      if (filter === 'company') {
        if (!cifRegex.test(cleanedTax) &&
          !(generalFormat.test(cleanedTax) && /^[A-Z]/.test(cleanedTax))) {
          return { error: 'fields.' + field + '.validations.invalid' };
        }
      } else if (filter === 'personal') {
        if (!nifRegex.test(cleanedTax) && !nieRegex.test(cleanedTax)) {
          return { error: 'fields.' + field + '.validations.invalid' };
        }
      } else {
        if (!generalFormat.test(cleanedTax)) {
          return { error: 'fields.' + field + '.validations.invalid' };
        }
      }

      return null;
    }
    // Função auxiliar para validar Equador
    function validateEC(tax: string, field: string): { [key: string]: any } | null {
      const regexPattern = /^[0-2]\d{9}$/;
      if (!regexPattern.test(tax)) {
        return { error: 'fields.' + field + '.validations.invalid' };
      }
      return null;
    }

    // Função auxiliar para validar Reino Unido
    function validateUK(tax: string, field: string, filter?: 'company' | 'personal'): { [key: string]: any } | null {
      // Remove o prefixo GB se existir e converte para maiúsculas
      const cleanedTax = tax.replace(/^GB/i, '').toUpperCase().trim();

      // Definir os diferentes formatos
      // Pessoa física
      const ninoRegex = /^[A-Z]{2}\d{6}[A-D]$/; // NINO: 2 letras + 6 dígitos + letra (A-D)
      const utrPersonalRegex = /^\d{10}$/; // UTR: 10 dígitos

      // Empresa
      const vatRegex = /^(\d{9}|\d{12})$/; // VAT: 9 ou 12 dígitos
      const vatSpecialRegex = /^[A-Z]{2}\d{3}$/; // VAT especial: 2 letras + 3 dígitos (GD, HA, etc)
      const crnRegex = /^(\d{8}|[A-Z]{2}\d{6})$/; // CRN: 8 dígitos ou 2 letras + 6 dígitos

      // Validação baseada no filtro
      if (filter === 'personal') {
        // Apenas formatos de pessoa física
        if (!ninoRegex.test(cleanedTax) && !utrPersonalRegex.test(cleanedTax)) {
          return { error: 'fields.' + field + '.validations.invalid' };
        }
      } else if (filter === 'company') {
        // Apenas formatos de empresa
        if (!vatRegex.test(cleanedTax) && !vatSpecialRegex.test(cleanedTax) &&
          !crnRegex.test(cleanedTax) && !utrPersonalRegex.test(cleanedTax)) {
          return { error: 'fields.' + field + '.validations.invalid' };
        }
      } else {
        // Sem filtro - aceita todos os formatos
        if (!ninoRegex.test(cleanedTax) && !utrPersonalRegex.test(cleanedTax) &&
          !vatRegex.test(cleanedTax) && !vatSpecialRegex.test(cleanedTax) &&
          !crnRegex.test(cleanedTax)) {
          return { error: 'fields.' + field + '.validations.invalid' };
        }
      }

      // Validações adicionais para NINO
      if (ninoRegex.test(cleanedTax)) {
        // NINO não pode ter estas combinações nas primeiras 2 letras
        const invalidPrefixes = ['BG', 'GB', 'NK', 'KN', 'TN', 'NT', 'ZZ'];
        const prefix = cleanedTax.substring(0, 2);
        if (invalidPrefixes.includes(prefix)) {
          return { error: 'fields.' + field + '.validations.invalid' };
        }
      }

      return null;
    }

    // Função auxiliar para validar Alemanha
    function validateDE(tax: string, field: string): { [key: string]: any } | null {
      // Remove o prefixo DE se existir
      const cleanedTax = tax.replace(/^DE/i, '');

      // Steuernummer: 10 or 11 digits
      // USt-IdNr (VAT): 9 digits (com ou sem prefixo DE)
      const steuernummerRegex = /^\d{10,11}$/;
      const vatRegex = /^\d{9}$/;

      if (!steuernummerRegex.test(cleanedTax) && !vatRegex.test(cleanedTax)) {
        return { error: 'fields.' + field + '.validations.invalid' };
      }
      return null;
    }

    // Função auxiliar para validar Brasil
    function validateBR(tax: string, field: string): { [key: string]: any } | null {
      // Remove formatação comum
      const cleanedTax = tax.replace(/[^\d]/g, '');

      // CPF: 11 dígitos
      if (cleanedTax.length === 11) {
        return validateCPF(cleanedTax) ? null : { error: 'fields.' + field + '.validations.invalid' };
      }

      // CNPJ: 14 dígitos
      if (cleanedTax.length === 14) {
        return validateCNPJ(cleanedTax) ? null : { error: 'fields.' + field + '.validations.invalid' };
      }

      return { error: 'fields.' + field + '.validations.invalid' };
    }

    // Validação de CPF (Brasil)
    function validateCPF(cpf: string): boolean {
      if (cpf.length !== 11) return false;

      // Verifica se todos os dígitos são iguais
      if (/^(\d)\1{10}$/.test(cpf)) return false;

      // Calcula primeiro dígito verificador
      let sum = 0;
      for (let i = 0; i < 9; i++) {
        sum += Number(cpf[i]) * (10 - i);
      }
      let digit1 = 11 - (sum % 11);
      if (digit1 > 9) digit1 = 0;

      // Calcula segundo dígito verificador
      sum = 0;
      for (let i = 0; i < 10; i++) {
        sum += Number(cpf[i]) * (11 - i);
      }
      let digit2 = 11 - (sum % 11);
      if (digit2 > 9) digit2 = 0;

      return digit1 === Number(cpf[9]) && digit2 === Number(cpf[10]);
    }

    // Validação de CNPJ (Brasil)
    function validateCNPJ(cnpj: string): boolean {
      if (cnpj.length !== 14) return false;

      // Verifica se todos os dígitos são iguais
      if (/^(\d)\1{13}$/.test(cnpj)) return false;

      // Calcula primeiro dígito verificador
      const weights1 = [5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];
      let sum = 0;
      for (let i = 0; i < 12; i++) {
        sum += Number(cnpj[i]) * weights1[i];
      }
      let digit1 = 11 - (sum % 11);
      if (digit1 > 9) digit1 = 0;

      // Calcula segundo dígito verificador
      const weights2 = [6, 5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];
      sum = 0;
      for (let i = 0; i < 13; i++) {
        sum += Number(cnpj[i]) * weights2[i];
      }
      let digit2 = 11 - (sum % 11);
      if (digit2 > 9) digit2 = 0;

      return digit1 === Number(cnpj[12]) && digit2 === Number(cnpj[13]);
    }

    // Função auxiliar para validar França
    function validateFR(tax: string, field: string, filter?: 'company' | 'personal'): { [key: string]: any } | null {
      // Remove formatação e prefixo FR se existir
      const cleanedTax = tax.replace(/[\s]/g, '').replace(/^FR/i, '');

      // NIF Pessoa Singular: 13 dígitos
      const nifPersonalRegex = /^\d{13}$/;

      // SIREN Pessoa Coletiva: 9 dígitos
      const sirenRegex = /^\d{9}$/;

      // SIRET (estabelecimento): 14 dígitos
      const siretRegex = /^\d{14}$/;

      // VAT: 2 caracteres + 9 dígitos (sem o prefixo FR)
      const vatRegex = /^[0-9A-Z]{2}\d{9}$/;

      // Validação baseada no filtro
      if (filter === 'personal') {
        // Apenas NIF de pessoa singular
        if (!nifPersonalRegex.test(cleanedTax)) {
          return { error: 'fields.' + field + '.validations.invalid' };
        }
      } else if (filter === 'company') {
        // SIREN, SIRET ou VAT
        if (!sirenRegex.test(cleanedTax) && !siretRegex.test(cleanedTax) && !vatRegex.test(cleanedTax)) {
          return { error: 'fields.' + field + '.validations.invalid' };
        }

        // Validação Luhn para SIREN/SIRET
        if ((sirenRegex.test(cleanedTax) || siretRegex.test(cleanedTax)) && !validateLuhn(cleanedTax)) {
          return { error: 'fields.' + field + '.validations.invalid' };
        }
      } else {
        // Sem filtro - aceita todos os formatos
        if (!nifPersonalRegex.test(cleanedTax) && !sirenRegex.test(cleanedTax) &&
          !siretRegex.test(cleanedTax) && !vatRegex.test(cleanedTax)) {
          return { error: 'fields.' + field + '.validations.invalid' };
        }

        // Validação Luhn apenas para SIREN/SIRET
        if ((sirenRegex.test(cleanedTax) || siretRegex.test(cleanedTax)) && !validateLuhn(cleanedTax)) {
          return { error: 'fields.' + field + '.validations.invalid' };
        }
      }

      return null;
    }

    // Algoritmo de Luhn para validação (usado na França)
    function validateLuhn(value: string): boolean {
      let sum = 0;
      let isEven = false;

      for (let i = value.length - 1; i >= 0; i--) {
        let digit = Number(value[i]);

        if (isEven) {
          digit *= 2;
          if (digit > 9) {
            digit -= 9;
          }
        }

        sum += digit;
        isEven = !isEven;
      }

      return sum % 10 === 0;
    }

    (taxNumberValidator as any).validatorName = 'taxNumberValidator';
    return taxNumberValidator;
  }

  // Validates if the name has spaces and doesn't have numbers
  static nameValidator(field: string, multipleNames: boolean = false, isCompany: boolean = false): ValidatorFn {
    const nameValidator: ValidatorFn = (control: AbstractControl): { [key: string]: any } | null => {
      const name = control.value;

      if (!name) {
        return null; // No value, valid by default
      }

      // Trim any accidental leading/trailing whitespace
      const trimmed = name.trim();

      let isValid: boolean;

      if (isCompany) {
        // Regex for company names: allows letters, numbers, spaces, apostrophes, hyphens, and common company characters
        const companyRegex = /^[A-Za-zÀ-ÿ0-9'&.,]+(?:[\s-][A-Za-zÀ-ÿ0-9'&.,]+)*$/;
        isValid = companyRegex.test(trimmed);
      } else {
        // Regex for multiple words (at least two names)
        const multiNameRegex = /^([A-Za-zÀ-ÿ']+[-']?[A-Za-zÀ-ÿ']*)(\s+([A-Za-zÀ-ÿ']+[-']?[A-Za-zÀ-ÿ']*))+$/;

        // Regex for valid name: only letters, accented characters, spaces, and apostrophes.
        const basicNameRegex = /^[A-Za-zÀ-ÿ']+(?: [A-Za-zÀ-ÿ']+)*(?:-[A-Za-zÀ-ÿ']+)*$/;

        isValid = multipleNames ? multiNameRegex.test(trimmed) : basicNameRegex.test(trimmed);
      }

      if (!isValid) {
        return { error: 'fields.' + field + '.validations.invalid' };
      }

      return null;
    };

    (nameValidator as any).validatorName = 'nameValidator';
    return nameValidator;
  }

  static startsWith(field: string, allowed: Array<any>): ValidatorFn {
    const startsWith: ValidatorFn = (control: AbstractControl): { [key: string]: any } | null => {
      let isValid = false;
      for (const item of allowed) {
        isValid = control.value.startsWith(item);
        if (isValid) {
          break;
        }
      }
      if (!isValid) {
        return { error: 'fields.' + field + '.validations.invalid' };
      }
      return null;
    };

    (startsWith as any).validatorName = 'startsWith';
    return startsWith;
  }

  static ibanValidator(field: string): ValidatorFn {
    const ibanValidator: ValidatorFn = (ibanControl: AbstractControl): { [key: string]: any } | null => {
      if (this.isValidIBANNumber(ibanControl.value) === false) {
        return { error: 'fields.' + field + '.validations.invalid' };
      }

      // A correctly formatted IBAN is only accepted when its country belongs to the SEPA zone (CLP-6815).
      return this.isSepaIBAN(ibanControl.value) ? null : { error: 'fields.' + field + '.validations.sepa' };
    };

    (ibanValidator as any).validatorName = 'ibanValidator';
    return ibanValidator;
  }

  static isSepaIBAN(input: any): boolean {
    // SEPA geographical scope (EPC): EU-27 + EEA (IS, LI, NO) + CH, MC, SM, AD, VA, GB, GI.
    const SEPA_COUNTRY_CODES: string[] = [
      'AD', 'AT', 'BE', 'BG', 'CH', 'CY', 'CZ', 'DE', 'DK', 'EE', 'ES', 'FI', 'FR', 'GB', 'GI', 'GR', 'HR', 'HU', 'IE',
      'IS', 'IT', 'LI', 'LT', 'LU', 'LV', 'MC', 'MT', 'NL', 'NO', 'PL', 'PT', 'RO', 'SE', 'SI', 'SK', 'SM', 'VA'
    ];

    const countryCode = String(input).toUpperCase().slice(0, 2);
    return SEPA_COUNTRY_CODES.includes(countryCode);
  }

  static isValidIBANNumber(input: any): boolean | number {
    const CODE_LENGTHS: any = {
      AD: 24,
      AE: 23,
      AT: 20,
      AZ: 28,
      BA: 20,
      BE: 16,
      BG: 22,
      BH: 22,
      BR: 29,
      CH: 21,
      CR: 21,
      CY: 28,
      CZ: 24,
      DE: 22,
      DK: 18,
      DO: 28,
      EE: 20,
      ES: 24,
      FI: 18,
      FO: 18,
      FR: 27,
      GB: 22,
      GI: 23,
      GL: 18,
      GR: 27,
      GT: 28,
      HR: 21,
      HU: 28,
      IE: 22,
      IL: 23,
      IS: 26,
      IT: 27,
      JO: 30,
      KW: 30,
      KZ: 20,
      LB: 28,
      LI: 21,
      LT: 20,
      LU: 20,
      LV: 21,
      MC: 27,
      MD: 24,
      ME: 22,
      MK: 19,
      MR: 27,
      MT: 31,
      MU: 30,
      NL: 18,
      NO: 15,
      PK: 24,
      PL: 28,
      PS: 29,
      PT: 25,
      QA: 29,
      RO: 24,
      RS: 22,
      SA: 24,
      SE: 24,
      SI: 19,
      SK: 24,
      SM: 27,
      TN: 24,
      TR: 26,
      AL: 28,
      BY: 28,
      EG: 29,
      GE: 22,
      IQ: 23,
      LC: 32,
      SC: 31,
      ST: 25,
      SV: 28,
      TL: 23,
      UA: 29,
      VA: 22,
      VG: 24,
      XK: 20
    };
    // keep only alphanumeric characters
    /*const iban = String(input).toUpperCase().replace(/[^A-Z0-9]/g, '');*/
    const iban = String(input).toUpperCase();
    // match and capture (1) the country code, (2) the check digits, and (3) the rest
    const code = iban.match(/^([A-Z]{2})(\d{2})([A-Z\d]+)$/);
    let digits = '';
    // check syntax and length
    if (!code || iban.length !== CODE_LENGTHS[code[1]]) {
      return false;
    }
    // rearrange country code and check digits, and convert chars to ints
    digits = (code[3] + code[1] + code[2]).replace(/[A-Z]/g, (letter: string) => {
      return (letter.charCodeAt(0) - 55).toString();
    });
    // final check
    return this.mod97(digits) === 1;
  }

  static mod97(code: any): number {
    let checksum = code.slice(0, 2);
    let fragment = '';
    for (let offset = 2; offset < code.length; offset += 7) {
      fragment = String(checksum) + code.substring(offset, offset + 7);
      checksum = parseInt(fragment, 10) % 97;
    }
    return checksum;
  }

  static maxLengthCheck(field: string, maxLength: number): ValidatorFn {
    const maxLengthCheck: ValidatorFn = (control: AbstractControl): { [key: string]: any } | null => {
      if (control.value?.length > maxLength) {
        return { error: 'fields.' + field + '.validations.invalidLength' };
      }
      return null;
    };

    (maxLengthCheck as any).validatorName = 'maxLengthCheck';
    return maxLengthCheck;
  }

  // serves to validate that the end Date cannot be lower than the Start Date
  static startDateBeforeEndDate(startField: string, endField: string): ValidatorFn {
    const startDateBeforeEndDate: ValidatorFn = (control: AbstractControl): { [key: string]: any } | null => {
      const startDateControl = control.parent?.get(startField)?.value;
      const endDateControl = control.parent?.get(endField)?.value;

      if (!startDateControl || !endDateControl) {
        return null;
      }
      const startDate = new Date(startDateControl);
      const endDate = new Date(endDateControl);

      if (startDate > endDate) {
        return {
          error: 'fields.' + endField + '.validations.out_of_range_min'
        };
      }

      return null;
    };

    (startDateBeforeEndDate as any).validatorName = 'startDateBeforeEndDate';
    return startDateBeforeEndDate;
  }

  static isValidUrl(field: string): ValidatorFn {
    const isValidUrl: ValidatorFn = (control: AbstractControl): { [key: string]: any } | null => {
      if (!control.value) {
        return null; // Valid if empty
      }

      const urlRegex = /^(https?:\/\/)?([a-zA-Z0-9-]+\.)+[a-zA-Z]{2,}(\/[^\s]*)?$/;

      if (urlRegex.test(control.value)) {
        return null; // Valid if it matches the regex
      }

      return { error: 'fields.' + field + '.validations.invalid' };
    };

    (isValidUrl as any).validatorName = 'isValidUrl';
    return isValidUrl;
  }

  static validConsent(field: string): ValidatorFn {
    const validConsent: ValidatorFn = (control: AbstractControl): { [key: string]: any } | null => {
      const value = control.value;

      // Check if value is an array and has at least one element
      if (!Array.isArray(value) || value.length === 0) {
        return { error: 'fields.' + field + '.validations.required' };
      }

      return null;
    };

    (validConsent as any).validatorName = 'validConsent';
    return validConsent;
  }
}
